import React from "react";
import { Link } from "react-router-dom";
import {
  FaInfoCircle,
  FaCode,
  FaCog,
  FaExclamationCircle,
  FaMobileAlt,
  FaShieldAlt,
} from "react-icons/fa";
import "../index.css";
import CodeBlock from "./CodeBlock";
import SignatureExample from "./SignatureExample";

const API = "https://saas.todoai.ai/api";

const FLOW = `1. Your backend creates a session with mode "headless" and gets a url.
2. Your page puts the url in an iframe inside any element you like.
3. The iframe sends TODO_AVATAR_READY with a channel id.
4. Your page sends TODO_AVATAR_START.
5. Your own interface captures the microphone or camera and produces the answer.
6. Your page sends the answer with TODO_AVATAR_SPEAK. The avatar says it with lip-sync.
7. The iframe reports TODO_AVATAR_SPEECH_STARTED and TODO_AVATAR_SPEECH_ENDED.
8. TODO_AVATAR_INTERRUPT stops the current speech. TODO_AVATAR_END closes the session.`;

const CREATE_REQUEST = `curl -X POST ${API}/avatar/embed/session \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: pk_YOUR_API_KEY" \\
  -H "X-Signature: $SIGNATURE" \\
  -d '{
    "avatar_id": "YOUR_AVATAR_ID",
    "domain": "YOUR_DOMAIN",
    "mode": "headless",
    "language": "it",
    "fit": "cover",
    "background": "transparent"
  }'`;

const CREATE_RESPONSE = `{
  "url": "https://avatar.example.com/embed/ONE_TIME_CODE/",
  "expires_in": 600
}`;

const IFRAME = `<div id="avatar-box" style="width:100%; max-width:420px; aspect-ratio:3/4">
  <iframe
    id="avatar-frame"
    src="https://avatar.example.com/embed/ONE_TIME_CODE/"
    style="width:100%; height:100%; border:0; background:transparent"
    allow="autoplay; fullscreen">
  </iframe>
</div>`;

const ENVELOPE_OUT = `{
  "source": "todoai-client",
  "version": "1.0",
  "channel": "<the channel from TODO_AVATAR_READY>",
  "type": "TODO_AVATAR_SPEAK",
  "request_id": "request-123",
  "payload": { "text": "Hello, how can I help you today?" }
}`;

const ENVELOPE_IN = `{
  "source": "todoai-avatar",
  "version": "1.0",
  "channel": "9f2c...",
  "type": "TODO_AVATAR_SPEECH_ENDED",
  "session_id": "SESSION_ID",
  "request_id": "request-123",
  "payload": {},
  "ts": 1760000000000
}`;

const VANILLA = `<div id="avatar-box" style="width:320px; aspect-ratio:3/4"></div>
<button id="talk">Say hello</button>

<script>
  // 1. Your backend returns the url (never create sessions in the browser).
  async function openAvatar(url) {
    const AVATAR_ORIGIN = new URL(url).origin;
    const frame = document.createElement("iframe");
    frame.src = url;
    frame.allow = "autoplay; fullscreen";
    frame.style.cssText = "width:100%;height:100%;border:0;background:transparent";
    document.getElementById("avatar-box").appendChild(frame);

    let channel = null;
    let counter = 0;
    const listeners = {};

    function send(type, payload) {
      const request_id = "req-" + ++counter;
      frame.contentWindow.postMessage(
        { source: "todoai-client", version: "1.0", channel, type, request_id, payload: payload || {} },
        AVATAR_ORIGIN // exact origin, never "*"
      );
      return request_id;
    }

    window.addEventListener("message", (event) => {
      // check both the origin and the window the message came from
      if (event.origin !== AVATAR_ORIGIN || event.source !== frame.contentWindow) return;
      const msg = event.data;
      if (!msg || msg.source !== "todoai-avatar") return;

      if (msg.type === "TODO_AVATAR_READY") {
        channel = msg.channel;
        send("TODO_AVATAR_START");
      }
      (listeners[msg.type] || []).forEach((fn) => fn(msg));
    });

    // Some browsers do not tell the frame its parent. Saying hello always works.
    frame.addEventListener("load", () => {
      frame.contentWindow.postMessage(
        { source: "todoai-client", version: "1.0", type: "TODO_AVATAR_HELLO" },
        AVATAR_ORIGIN
      );
    });

    return {
      on: (type, fn) => (listeners[type] = (listeners[type] || []).concat(fn)),
      speak: (text) => send("TODO_AVATAR_SPEAK", { text }),
      interrupt: () => send("TODO_AVATAR_INTERRUPT"),
      mute: () => send("TODO_AVATAR_MUTE"),
      unmute: () => send("TODO_AVATAR_UNMUTE"),
      end: () => send("TODO_AVATAR_END"),
    };
  }

  // 2. Use it with your own interface.
  (async () => {
    const { url } = await (await fetch("/my-backend/create-avatar-session", { method: "POST" })).json();
    const avatar = await openAvatar(url);

    avatar.on("TODO_AVATAR_STARTED", () => avatar.speak("Hello, how can I help you today?"));
    avatar.on("TODO_AVATAR_SPEECH_ENDED", () => console.log("done speaking, listen again"));
    avatar.on("TODO_AVATAR_USER_GESTURE_REQUIRED", () => console.log("show your own start button"));
    avatar.on("TODO_AVATAR_ERROR", (m) => console.warn(m.payload.code, m.payload.message));

    document.getElementById("talk").onclick = () => avatar.speak("Hello again!");
    window.addEventListener("pagehide", () => avatar.end());
  })();
</script>`;

const REACT = `import { useCallback, useEffect, useRef, useState } from "react";

// url: the link your backend got from POST /api/avatar/embed/session
export function useAvatar(url) {
  const frameRef = useRef(null);
  const channel = useRef(null);
  const counter = useRef(0);
  const [status, setStatus] = useState("loading"); // loading | ready | live | speaking | ended
  const [error, setError] = useState(null);
  const origin = new URL(url).origin;

  const send = useCallback(
    (type, payload = {}) => {
      const frame = frameRef.current && frameRef.current.contentWindow;
      if (!frame) return null;
      const request_id = "req-" + ++counter.current;
      frame.postMessage(
        { source: "todoai-client", version: "1.0", channel: channel.current, type, request_id, payload },
        origin
      );
      return request_id;
    },
    [origin]
  );

  useEffect(() => {
    function onMessage(event) {
      if (event.origin !== origin || event.source !== frameRef.current?.contentWindow) return;
      const msg = event.data;
      if (!msg || msg.source !== "todoai-avatar") return;
      switch (msg.type) {
        case "TODO_AVATAR_READY":
          channel.current = msg.channel;
          setStatus("ready");
          send("TODO_AVATAR_START");
          break;
        case "TODO_AVATAR_STARTED": setStatus("live"); break;
        case "TODO_AVATAR_SPEECH_STARTED": setStatus("speaking"); break;
        case "TODO_AVATAR_SPEECH_ENDED": setStatus("live"); break;
        case "TODO_AVATAR_ENDED": setStatus("ended"); break;
        case "TODO_AVATAR_ERROR": setError(msg.payload); break;
        default: break;
      }
    }
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
      send("TODO_AVATAR_END"); // release the session when the component goes away
    };
  }, [origin, send]);

  const hello = () => send("TODO_AVATAR_HELLO");
  return {
    frameRef, status, error, hello,
    speak: (text) => send("TODO_AVATAR_SPEAK", { text }),
    interrupt: () => send("TODO_AVATAR_INTERRUPT"),
    end: () => send("TODO_AVATAR_END"),
  };
}

export default function AvatarBox({ url }) {
  const { frameRef, status, speak, interrupt, hello } = useAvatar(url);
  return (
    <div>
      <div style={{ width: 320, aspectRatio: "3 / 4" }}>
        <iframe
          ref={frameRef}
          src={url}
          onLoad={hello}
          allow="autoplay; fullscreen"
          style={{ width: "100%", height: "100%", border: 0, background: "transparent" }}
        />
      </div>
      <p>Status: {status}</p>
      <button onClick={() => speak("Hello! How can I help?")}>Speak</button>
      <button onClick={interrupt}>Stop talking</button>
    </div>
  );
}

// Next.js: put this component in a client component ("use client") and create the
// session on the server, for example in a route handler or server action.`;

const NEXT_SERVER = `// app/api/avatar-session/route.js  (runs on your server only)
import crypto from "crypto";

export async function POST() {
  const domain = "YOUR_DOMAIN";
  const signature = crypto
    .createHmac("sha256", process.env.TODOAI_SECRET_KEY) // never in browser code
    .update(domain)
    .digest("hex");

  const res = await fetch("${API}/avatar/embed/session", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-API-Key": process.env.TODOAI_API_KEY,
      "X-Signature": signature,
    },
    body: JSON.stringify({
      avatar_id: process.env.TODOAI_AVATAR_ID,
      domain,
      mode: "headless",
    }),
  });
  return Response.json(await res.json(), { status: res.status });
}`;

const IOS = `// The page is a normal WebView. Events arrive through the "todoai" message handler.
config.userContentController.add(self, name: "todoai")
config.allowsInlineMediaPlayback = true
config.mediaTypesRequiringUserActionForPlayback = []

func userContentController(_ c: WKUserContentController, didReceive message: WKScriptMessage) {
    guard let body = message.body as? [String: Any], let type = body["type"] as? String else { return }
    // body["channel"], body["request_id"], body["payload"] ...
}

// Commands: call the page functions.
webView.evaluateJavaScript("window.todoai.start()")
webView.evaluateJavaScript("window.todoai.speak('Hello!')")
webView.evaluateJavaScript("window.todoai.interrupt()")
webView.evaluateJavaScript("window.todoai.stop()")`;

const ANDROID = `webView.settings.javaScriptEnabled = true
webView.settings.mediaPlaybackRequiresUserGesture = false
webView.addJavascriptInterface(object {
    @JavascriptInterface
    fun postMessage(json: String) {
        // json is the event: type, channel, request_id, payload
    }
}, "TodoAI")

// Commands:
webView.evaluateJavascript("window.todoai.speak('Hello!')", null)
webView.evaluateJavascript("window.todoai.interrupt()", null)
webView.evaluateJavascript("window.todoai.stop()", null)`;

const SPEAK_RESULTS = `// Your backend calls the TODO AI APIs (API key + signature) and returns the JSON and status.
// "avatar" is the object from the examples above.

// 1. A typed or spoken question: Generate Response
//    POST /api/agents/generate-response/  { domain, lang, text_query | audio_file }
const answer = await myBackend.generateResponse(question);
if (answer.ok) {
  avatar.speak(answer.data.response);        // TODO_AVATAR_SPEAK with that text
}

// 2. A face recording: Process Video
//    POST /api/agents/process-video/      { domain, lang, video_file }
const result = await myBackend.processVideo(videoFile);
if (result.ok) {                              // HTTP 200 only
  const { analysis, rag_query } = result.data.analysis;
  const failed = !analysis || analysis === "Analysis could not be parsed correctly.";

  if (!failed) {
    avatar.speak(analysis);                   // say the analysis first

    // 3. Then answer the follow-up question the analysis suggests
    if (rag_query) {
      const followUp = await myBackend.generateResponse(rag_query);
      if (followUp.ok) avatar.speak(followUp.data.response);
    }
  }
}`;

const Required = () => (
  <span className="docs-badge docs-badge-required">Required</span>
);
const Optional = () => <span className="docs-badge">Not Required</span>;

const RequestTable = ({ title, rows }) => (
  <div className="docs-table">
    <h4 className="docs-response-title">{title}</h4>
    <table>
      <thead>
        <tr>
          <th>Field</th>
          <th>Type</th>
          <th>Required</th>
          <th>Description</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([name, type, required, description]) => (
          <tr key={name}>
            <td>{name}</td>
            <td>
              <code>{type}</code>
            </td>
            <td>{required ? <Required /> : <Optional />}</td>
            <td>{description}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const SimpleTable = ({ title, head, rows }) => (
  <div className="docs-table">
    {title && <h4 className="docs-response-title">{title}</h4>}
    <table>
      <thead>
        <tr>
          {head.map((h) => (
            <th key={h}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row[0]}>
            {row.map((cell, i) => (
              <td key={i}>{i === 0 ? <code>{cell}</code> : cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const AvatarEmbedded = () => {
  return (
    <div className="docs-page">
      <div className="docs-page-header">
        <h1 className="docs-page-title">Embedded Avatar (Headless)</h1>
        <p className="docs-page-description">
          Put only the avatar inside your own page or app. You build the
          buttons, microphone, camera and conversation, and control the avatar
          with messages.
        </p>
      </div>

      <div className="docs-section">
        <h2 className="docs-section-title">
          <FaInfoCircle /> How It Works
        </h2>
        <CodeBlock title="Flow" code={FLOW} />
        <p>
          Want a ready-made page instead? Use the{" "}
          <Link to="/docs/avatar/hosted">Hosted Avatar</Link>.
        </p>
      </div>

      <div className="docs-section">
        <h2 className="docs-section-title">
          <FaCode /> Create a Headless Session
        </h2>
        <div className="docs-endpoint">
          <div className="docs-endpoint-header">
            <span className="docs-endpoint-method post">POST</span>
            <code className="docs-endpoint-url">/api/avatar/embed/session</code>
          </div>
          <p className="docs-endpoint-description">
            Call this from your backend with your API key and signature, as in
            the <Link to="/docs/avatar/hosted">Hosted Avatar</Link> guide.
          </p>
          <RequestTable
            title="Request Body (JSON)"
            rows={[
              ["avatar_id", "string", true, "Id from /api/avatar/list."],
              ["domain", "string", true, "Your business domain, enabled for your account. Also the signature payload."],
              ["mode", "string", false, 'Default "hosted" (the full page). Send "headless" for the avatar only.'],
              ["language", "string", false, "A code from /api/agents/supported-languages/. Default: English (en)."],
              ["fit", "string", false, '"contain" (default) or "cover". Headless only. See Background and Fit.'],
              ["background", "string", false, '"transparent" (default) or a hex color such as "#101010". Headless only.'],
              ["gesture_overlay", "boolean", false, 'Default true: the iframe shows a small "Tap to enable sound" button when the browser blocks sound. Set false to use your own button. Headless only.'],
            ]}
          />
          <CodeBlock title="Example Request" code={CREATE_REQUEST} />
          <CodeBlock title="200 OK Response" code={CREATE_RESPONSE} />
          <p>
            The session can be started once from the link, and an unopened link
            expires after <code>expires_in</code> seconds. The avatar starts when you send{" "}
            <code>TODO_AVATAR_START</code>. To limit which sites can embed it,
            set "Allowed sites" in{" "}
            <Link to="/avatar-studio">Avatar Studio</Link> (exact origins, for
            example <code>https://shop.example.com</code>). Without it, any site
            with the link can embed it.
          </p>
        </div>
      </div>

      <div className="docs-section">
        <h2 className="docs-section-title">
          <FaCode /> Embed the Iframe
        </h2>
        <p>
          The iframe fills its container. Set the size and shape with your own
          CSS.
        </p>
        <CodeBlock title="HTML" code={IFRAME} />
      </div>

      <div className="docs-section">
        <h2 className="docs-section-title">
          <FaCog /> Background and Fit
        </h2>
        <p>
          The video always has its own scene behind the person. You control the
          empty space around it.
        </p>
        <SimpleTable
          head={["Option", "Result"]}
          rows={[
            ["fit: contain", "Shows the whole video. Bars appear if your container has a different shape."],
            ["fit: cover", "Fills the container and crops the edges. No bars."],
            ["background: transparent", "The bars are see-through, so your page shows through."],
            ["background: #rrggbb", "The bars use that color."],
          ]}
        />
        <p>
          For an exact fit, give the container the video's shape (CSS{" "}
          <code>aspect-ratio</code>) and use <code>cover</code>. Style corners
          and shadows on the container, and keep the iframe itself transparent
          with no border.
        </p>
      </div>

      <div className="docs-section">
        <h2 className="docs-section-title">
          <FaShieldAlt /> Messages and Security
        </h2>
        <p>
          Messages use <code>window.postMessage</code>. Each has a{" "}
          <code>version</code>, a <code>type</code> and a{" "}
          <code>request_id</code> you choose (up to 64 characters). Events
          carry the same <code>request_id</code>.
        </p>
        <CodeBlock title="Your page to the iframe" code={ENVELOPE_OUT} />
        <CodeBlock title="The iframe to your page" code={ENVELOPE_IN} />
        <ul className="docs-list">
          <li>
            Accept events only if <code>event.origin</code> is the iframe's
            origin and <code>event.source</code> is your iframe's{" "}
            <code>contentWindow</code>.
          </li>
          <li>
            Send commands with the iframe's origin as{" "}
            <code>targetOrigin</code>, never <code>"*"</code>.
          </li>
          <li>
            The <code>channel</code> in <code>TODO_AVATAR_READY</code> is random
            per page load. Every command except{" "}
            <code>TODO_AVATAR_HELLO</code> must include it.
          </li>
          <li>
            Limits: 2000 characters per <code>TODO_AVATAR_SPEAK</code>, 40
            commands per 10 seconds.
          </li>
          <li>The iframe never gives your page any key or token.</li>
          <li>
            The iframe sends <code>TODO_AVATAR_READY</code> by itself. Also send{" "}
            <code>TODO_AVATAR_HELLO</code> when the iframe loads, because some
            browsers hide your origin from it.
          </li>
        </ul>
      </div>

      <div className="docs-section">
        <h2 className="docs-section-title">
          <FaCog /> Commands
        </h2>
        <SimpleTable
          head={["type", "payload", "What it does"]}
          rows={[
            ["TODO_AVATAR_HELLO", "-", "Asks for TODO_AVATAR_READY. No channel needed."],
            ["TODO_AVATAR_START", "-", "Connects the avatar. Answer: TODO_AVATAR_STARTED."],
            ["TODO_AVATAR_SPEAK", "{ text }", "Says exactly this text with lip-sync. Queued in order."],
            ["TODO_AVATAR_INTERRUPT", "-", "Stops the current speech and clears the queue."],
            ["TODO_AVATAR_MUTE", "-", "Mutes the sound. Answer: TODO_AVATAR_ACK."],
            ["TODO_AVATAR_UNMUTE", "-", "Turns the sound on. Answer: TODO_AVATAR_ACK."],
            ["TODO_AVATAR_END", "-", "Ends the session. Safe to send more than once."],
          ]}
        />
      </div>

      <div className="docs-section">
        <h2 className="docs-section-title">
          <FaCog /> Events
        </h2>
        <SimpleTable
          head={["type", "payload", "When"]}
          rows={[
            ["TODO_AVATAR_READY", "{ capabilities }", "The iframe loaded. Carries your channel."],
            ["TODO_AVATAR_STARTED", "{ resumed }", "Connected. resumed is true after a refresh."],
            ["TODO_AVATAR_SPEECH_STARTED", "{}", "The avatar began a SPEAK (same request_id)."],
            ["TODO_AVATAR_SPEECH_ENDED", "{}", "The avatar finished it. Listen again here."],
            ["TODO_AVATAR_INTERRUPTED", "{ interrupted_request_id }", "Your INTERRUPT stopped the speech, or null if none."],
            ["TODO_AVATAR_ACK", "{ command }", "A MUTE or UNMUTE was applied."],
            ["TODO_AVATAR_ENDED", "{ reason }", "Session over. reason: ended (you ended it) or disconnected (the connection dropped, including at the maximum duration)."],
            ["TODO_AVATAR_USER_GESTURE_REQUIRED", "{}", "The browser blocked sound until a tap."],
            ["TODO_AVATAR_ERROR", "{ code, message, recoverable }", "A command or the session failed."],
          ]}
        />
        <SimpleTable
          title="Error codes"
          head={["code", "What to do"]}
          rows={[
            ["INVALID_MESSAGE", "Fix the message, or use the right channel."],
            ["UNSUPPORTED_VERSION", "Use version 1.0."],
            ["NOT_STARTED", "Send TODO_AVATAR_START first."],
            ["NOT_CONNECTED", "Retry in a moment."],
            ["TEXT_TOO_LONG", "Send up to 2000 characters."],
            ["RATE_LIMITED", "Wait a few seconds."],
            ["SESSION_EXPIRED", "Ask your backend for a new link."],
            ["CONNECTION_FAILED", "Create a new session and try again."],
            ["SESSION_START_FAILED", "The avatar could not start. Create a new session and try again."],
          ]}
        />
      </div>

      <div className="docs-section">
        <h2 className="docs-section-title">
          <FaCode /> Speak Generate Response and Process Video Results
        </h2>
        <p>
          The avatar says the text you send. To let it answer questions or
          report a face analysis, call these APIs from your backend and send
          the text to the avatar with <code>TODO_AVATAR_SPEAK</code>.
        </p>
        <SimpleTable
          head={["API", "Text to speak"]}
          rows={[
            [
              "Generate Response",
              "response, from a 200 OK answer. Send the same language code as lang that you used for the session.",
            ],
            [
              "Process Video",
              "analysis.analysis, from a 200 OK answer. Then send analysis.rag_query as text_query to Generate Response and speak its response. Skip an empty rag_query.",
            ],
          ]}
        />
        <p>
          Speak only a successful (200 OK) answer. Other statuses carry an{" "}
          <code>error</code> field and no text to speak. Do not speak{" "}
          <code>analysis.analysis</code> if it is empty or reads "Analysis could
          not be parsed correctly."
        </p>
        <p>
          Details and examples:{" "}
          <a href="/docs/agent/generate-response">
            Generate Conversational Response
          </a>{" "}
          and <a href="/docs/agent/process-video">Process Video</a>. Both use
          the same API key and signature as session creation.
        </p>
        <CodeBlock title="JavaScript" code={SPEAK_RESULTS} scroll />
      </div>

      <div className="docs-section">
        <h2 className="docs-section-title">
          <FaCode /> Vanilla JavaScript Example
        </h2>
        <CodeBlock title="HTML and JavaScript" code={VANILLA} scroll />
      </div>

      <div className="docs-section">
        <h2 className="docs-section-title">
          <FaCode /> React and Next.js Example
        </h2>
        <CodeBlock title="React hook and component" code={REACT} scroll />
        <CodeBlock title="Next.js route handler (server)" code={NEXT_SERVER} scroll />
      </div>

      <div className="docs-section">
        <h2 className="docs-section-title">
          <FaExclamationCircle /> Sound, Permissions and Cleanup
        </h2>
        <ul className="docs-list">
          <li>
            <strong>Sound.</strong> Browsers block sound until a tap. If it is
            blocked, the video plays silently and you get{" "}
            <code>TODO_AVATAR_USER_GESTURE_REQUIRED</code>. Show your own button
            and send <code>TODO_AVATAR_UNMUTE</code>, or keep the built-in
            button. Safari on iOS may still need a tap on it.
          </li>
          <li>
            <strong>Microphone and camera.</strong> The iframe never uses them.
            Your page captures them, creates the answer, and sends it with{" "}
            <code>TODO_AVATAR_SPEAK</code>. Pause your listening between{" "}
            <code>SPEECH_STARTED</code> and <code>SPEECH_ENDED</code> to avoid
            echo.
          </li>
          <li>
            <strong>Refresh.</strong> The iframe reconnects to the same session
            and sends <code>TODO_AVATAR_STARTED</code> with{" "}
            <code>resumed: true</code>. Sound may need another tap.
          </li>
          <li>
            <strong>Cleanup.</strong> Send <code>TODO_AVATAR_END</code> when the
            user leaves. If the page closes without it, the session stops
            automatically after about 30 seconds, or at its maximum duration.
          </li>
          <li>
            <strong>Recovery.</strong> After <code>TODO_AVATAR_ENDED</code> or a
            non-recoverable error, get a new link and create a new iframe.
          </li>
        </ul>
      </div>

      <div className="docs-section">
        <h2 className="docs-section-title">
          <FaMobileAlt /> Mobile WebViews
        </h2>
        <p>
          Load the link in a WebView. Events arrive in a native message handler
          as the same JSON, and you send commands with{" "}
          <code>window.todoai</code>. Enable inline media playback and autoplay.
          If an iframe is not suitable, use the{" "}
          <Link to="/docs/avatar/hosted">hosted page with a redirect</Link>.
        </p>
        <CodeBlock title="iOS (WKWebView)" code={IOS} scroll />
        <CodeBlock title="Android (WebView)" code={ANDROID} scroll />
      </div>

      <div className="docs-section">
        <h2 className="docs-section-title">
          <FaExclamationCircle /> Versioning
        </h2>
        <ul className="docs-list">
          <li>
            Every message has its version. This guide describes <code>1.0</code>.
          </li>
          <li>
            Within version 1 we only add events, commands and optional fields.
            Ignore ones you do not know.
          </li>
          <li>
            A breaking change gets a new major version, and version 1 keeps
            working until we announce its end well in advance.
          </li>
        </ul>
      </div>

      <SignatureExample mode="headless" />
    </div>
  );
};

export default AvatarEmbedded;
